import { Entity, EntityClass, EntityType } from './entities.model';
import { TablePattern } from './table-patterns';
import { SportCategory } from './categories.model';
import { Document } from '../features/documents/models/document.models';
import { ReceivableType } from '../features/receivables/models/receivable.model';
import { ContributorJob } from './contributor-jobs.model';
import { Indexable } from '../core/models/indexable';

// tslint:disable: variable-name

export type RelationStatus = 'PENDING'
  | 'ACCEPTED' | 'REJECTED' | 'CANCELED'
  | 'ENDED' | 'OPEN_TICKET' | 'ACTIVE'
  | 'TRANSFER_OUT' | 'TRANSFER_IN'
  | 'QUIT_REQUESTED' | 'FIRED_REQUESTED'
  | 'WAITING_LOAN';


export type RelationMode = 'parent' | 'child';
export type RelationType = 'sources' | 'student' | 'contractor' | 'family' | 'supporter';
export type RelationLoanType = 'O' | 'D';
export const ActiveLikeRelations: RelationStatus[] = ['ACCEPTED', 'ACTIVE', 'FIRED_REQUESTED', 'PENDING', 'QUIT_REQUESTED'];

export const RELATION_LOAN_TYPE: Indexable<RelationLoanType, string> = {
  O: 'Emprestado', //is origin relation of loan
  D: 'Emprestada' // is destination relation of loan
}

export const RELATION_STATUS: Indexable<RelationStatus, string> = {
  ACCEPTED: 'Aceito',
  ACTIVE: 'Ativo',
  CANCELED: 'Cancelado',
  ENDED: 'Finalizado',
  FIRED_REQUESTED: 'Pediu para sair',
  QUIT_REQUESTED: 'Saindo',
  OPEN_TICKET: 'Em Aberto',
  PENDING: 'Aguardando',
  REJECTED: 'Rejeitado',
  TRANSFER_IN: 'Em Transferencia: Entrando',
  TRANSFER_OUT: 'Em Tranferencia: Saindo',
  WAITING_LOAN: 'Aguardando Empréstimo'
}
export interface RelationContractOptions {
  receivable_type: ReceivableType;
}

export class Relation<P = Entity, C = Entity> extends TablePattern {
  parent_id: number;
  child_id: number;
  status: RelationStatus;
  jobs?: string;
  started_at: string;
  ended_at: null;
  active: boolean;
  shirt_number: string;
  field_position: string;
  requester_id: number;
  contract_options: RelationContractOptions;
  relation_type: RelationType;
  legal_responsible?: boolean;
  job_id?: number;
  photo_url: string;
  loan_type: RelationLoanType;

  parent?: P;
  child?: C;
  entity?: C;
  contributor_job?: ContributorJob;

  constructor(o?: Partial<Relation>) {
    super(o, { int: ['parent_id', 'child_id'] });
  }

  public getFieldPosition(viewType: 'entity' | 'relation' | 'entity-relation' | 'relation-entity') {
    if (viewType == 'entity') { return this.entity ? (this.entity as any).field_position : ''; }
    if (viewType == 'relation') { return this.field_position; }
    if (viewType == 'entity-relation') {
      if (this.field_position) { return this.field_position; }
      if (this.entity && (this.entity as any).field_position) { return (this.entity as any).field_position; }
      return '';
    }

    if (viewType == 'relation-entity') {
      if (this.field_position) { return this.field_position; }
      if (this.entity && (this.entity as any).field_position) { return (this.entity as any).field_position; }
      return '';
    }
    return this.field_position;
  }

  public getFootTypeName() {
    if (!this.entity) { return ''; }
    const FT = {
      R: 'Destro',
      L: 'Canhoto',
      B: 'Ambidestro'
    };

    return FT[(this.entity as any).foot_type];
  }
}

export class RelationInfo extends Relation {
  child?: EntityClass;
  parent?: EntityClass;
  documents?: Document[];
}

export class GenericRelation extends Entity {

  constructor(o: Partial<GenericRelation>, normalize?: { int?: string[], dates?: string[] }) {
    if (!normalize) { normalize = { int: [] }; }
    normalize.int.push(...['relation_id', 'parent_id', 'child_id', 'requester_id']);
    super(o, normalize);
  }
  documents: Document;
  relation_id: number;
  parent_id: number;
  child_id: number;
  requester_id: number;
  status: RelationStatus;
  parent?: Entity;
  child?: Entity;
  active?: boolean;
  jobs?: string;
  relation?: Relation;
  category?: SportCategory;
  field_position?: string;
  foot_type?: string;
  job?: ContributorJob;
  crm?: string;
  crm_uf?: string;

  public static getActionName(status: RelationStatus) {
    const s = {
      A: 'Aceitanto',
      R: 'Rejeitando',
      C: 'Cancelando',
      T: 'Terminando'
    };
    return s[status];
  }

  public static categorizeAtletes(athletes: GenericRelation[], categories: SportCategory[]) {
    // sort desc to fit on the minor option
    const _categories = categories.sort((a, b) => a.max < b.max ? -1 : 1);
    return athletes.map(_a => {
      if (!_a.birth) {
        return;
      }

      const age = new Date().getFullYear() - new Date(_a.birth).getFullYear();

      _a.category = _categories.find((_c) => {
        const found = (age >= _c.min && age <= _c.max);
        return found;
      });

      return _a;
    });
  }

  public getStatusName() {
    const s = {
      PENDING: 'Pendente',
      ACCEPTED: 'Aceito',
      REJECTED: 'Rejeitado',
      CANCELED: 'Cancelado',
      ENDED: 'Terminado',
      'OPEN TICKET': 'Ag. Pagamento',
      TRANSFER_IN: 'Em transferencia',
      TRANSFER_OUT: 'Saindo',
      QUIT_REQUESTED: 'Desligando',
      FIRED_REQUESTED: 'Desligando',
      ACTIVE: 'Ativo'
    };
    return s[this.status];
  }

  public getFieldPosition(viewType: 'entity' | 'relation' | 'entity-relation' | 'relation-entity') {
    if (viewType == 'entity') { return this.field_position; }
    if (viewType == 'relation') { return this.relation ? this.relation.field_position : ''; }
    if (viewType == 'entity-relation') {
      if (this.field_position) { return this.field_position; }
      if (this.relation) { return this.relation.field_position; }
      return '';
    }

    if (viewType == 'relation-entity') {
      if (this.relation && this.relation.field_position) { return this.relation.field_position; }
      if (this.field_position) { return this.field_position; }
      return '';
    }
    return this.field_position;
  }

  public getFootTypeName() {
    const FT = {
      R: 'Destro',
      L: 'Canhoto',
      B: 'Ambidestro'
    };

    return FT[this.foot_type];
  }
}

interface RelationValidationOptions {
  [index: string]: { childs: EntityType[], parents: EntityType[] };
}

export const ValidRelations: RelationValidationOptions = {
  athletes: {
    childs: [],
    parents: ['clubs', 'schools']
  },
  clubs: {
    childs: ['athletes', 'contributors', 'users'],
    parents: ['leagues']
  },
  contributors: {
    childs: [],
    parents: ['clubs']
  },
  leagues: {
    childs: ['clubs', 'leagues', 'referees', 'users'],
    parents: ['leagues']
  },
  referees: {
    childs: [],
    parents: ['leagues']
  },
  schools: {
    childs: ['athletes', 'users'],
    parents: ['clubs']
  },
  users: {
    childs: [],
    parents: []
  },
};
